import { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Pagination,
    Select,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import axiosInstance from '../../helpers/axios';

const PAGE_LIMIT = 20;
const SEARCH_DEBOUNCE_MS = 300;

// Keeps long option lists from overflowing the viewport and getting shifted above the modal.
const SELECT_MENU_PROPS = {
    variant: 'menu',
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
    PaperProps: { style: { maxHeight: 250 } },
};

const AUTOCOMPLETE_POPPER_PROPS = {
    placement: 'bottom-start',
    modifiers: [{ name: 'flip', enabled: false }],
};

// Explicit (rather than relying on MUI's default) so matching institutes always narrow
// down as soon as the search text changes, regardless of list size.
const filterInstituteOptions = createFilterOptions({
    stringify: (option) => option.brandName || '',
});

function formatToLakhs(amount) {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${parseFloat((amount / 10000000).toFixed(2))} Cr`;
    if (amount >= 100000) return `₹${parseFloat((amount / 100000).toFixed(2))} L`;
    return `₹${Number(amount).toLocaleString('en-IN')}`;
}

function formatDate(value) {
    if (!value) return '—';
    return new Date(value).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
}

function LoanTableSkeleton() {
    return (
        <TableBody>
            {Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                    {Array.from({ length: 8 }).map((_, j) => (
                        <TableCell key={j}><Skeleton variant="text" /></TableCell>
                    ))}
                </TableRow>
            ))}
        </TableBody>
    );
}

export default function ManualAssignModal({ open, onClose, onSuccess }) {
    const [funds, setFunds] = useState([]);
    const [selectedFundId, setSelectedFundId] = useState('');

    const [institutes, setInstitutes] = useState([]);
    const [selectedInstituteId, setSelectedInstituteId] = useState('');

    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const [loans, setLoans] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [isLoadingLoans, setIsLoadingLoans] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const [selectedLoanIds, setSelectedLoanIds] = useState(() => new Set());
    const [selectedLoanAmounts, setSelectedLoanAmounts] = useState(() => new Map());

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [partialResult, setPartialResult] = useState(null);

    useEffect(() => {
        if (!open) return;
        setSelectedFundId('');
        setSelectedInstituteId('');
        setSearchInput('');
        setDebouncedSearch('');
        setPage(1);
        setSelectedLoanIds(new Set());
        setSelectedLoanAmounts(new Map());
        setSubmitError(null);
        setPartialResult(null);

        axiosInstance
            .get('/admin-settings/funds')
            .then((response) => setFunds(response.data.data.funds))
            .catch(() => setFunds([]));

        axiosInstance
            .get('/institute/info', { params: { perPage: '200', pageNo: '1' } })
            .then((response) => setInstitutes(response.data.data.institutes))
            .catch(() => setInstitutes([]));
    }, [open]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchInput.trim());
            setPage(1);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        setPage(1);
    }, [selectedInstituteId]);

    const loadLoans = useCallback(() => {
        if (!open) return;
        setIsLoadingLoans(true);
        setLoadError(null);
        axiosInstance
            .get('/admin-settings/loan-hypothecation/available', {
                params: {
                    page,
                    limit: PAGE_LIMIT,
                    search: debouncedSearch || undefined,
                    instituteId: selectedInstituteId || undefined,
                },
            })
            .then((response) => {
                setLoans(response.data.data.loans);
                setTotal(response.data.data.total);
            })
            .catch(() => setLoadError('Could not load free loans.'))
            .finally(() => setIsLoadingLoans(false));
    }, [open, page, debouncedSearch, selectedInstituteId]);

    useEffect(() => {
        loadLoans();
    }, [loadLoans]);

    const toggleLoan = (loan) => {
        setSelectedLoanIds((prev) => {
            const next = new Set(prev);
            if (next.has(loan.loanId)) next.delete(loan.loanId);
            else next.add(loan.loanId);
            return next;
        });
        setSelectedLoanAmounts((prev) => {
            const next = new Map(prev);
            if (next.has(loan.loanId)) next.delete(loan.loanId);
            else next.set(loan.loanId, loan.amount || 0);
            return next;
        });
    };

    const allOnPageSelected = loans.length > 0 && loans.every((l) => selectedLoanIds.has(l.loanId));

    const toggleSelectAllOnPage = () => {
        setSelectedLoanIds((prev) => {
            const next = new Set(prev);
            if (allOnPageSelected) loans.forEach((l) => next.delete(l.loanId));
            else loans.forEach((l) => next.add(l.loanId));
            return next;
        });
        setSelectedLoanAmounts((prev) => {
            const next = new Map(prev);
            if (allOnPageSelected) loans.forEach((l) => next.delete(l.loanId));
            else loans.forEach((l) => next.set(l.loanId, l.amount || 0));
            return next;
        });
    };

    const selectedCount = selectedLoanIds.size;
    const selectedTotalValue = [...selectedLoanAmounts.values()].reduce((sum, v) => sum + v, 0);
    const selectedFund = funds.find((f) => f.id === selectedFundId);

    const canAssign = selectedCount > 0 && Boolean(selectedFundId) && !isSubmitting;

    const handleAssign = () => {
        setIsSubmitting(true);
        setSubmitError(null);
        setPartialResult(null);
        axiosInstance
            .post('/admin-settings/loan-hypothecation/bulk-hypothecate', {
                fundId: selectedFundId,
                loanIds: [...selectedLoanIds],
            })
            .then((response) => {
                const { success, skipped, errors } = response.data.data;
                if (skipped > 0 || errors.length > 0) {
                    setPartialResult({ success, skipped, errors });
                    onSuccess();
                } else {
                    onSuccess(`Successfully assigned ${success} loans to ${selectedFund ? selectedFund.name : ''}`);
                    onClose();
                }
            })
            .catch((error) => {
                setSubmitError(error?.response?.data?.message || 'Could not assign loans.');
            })
            .finally(() => setIsSubmitting(false));
    };

    const start = total === 0 ? 0 : (page - 1) * PAGE_LIMIT + 1;
    const end = Math.min(page * PAGE_LIMIT, total);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Manual Assign — Select Loans to Hypothecate</DialogTitle>
            <DialogContent>
                {submitError && <Alert severity="error" sx={{ marginBottom: '16px' }}>{submitError}</Alert>}
                {partialResult && (
                    <Alert severity="warning" sx={{ marginBottom: '16px' }}>
                        Assigned {partialResult.success}, skipped {partialResult.skipped} (already assigned)
                    </Alert>
                )}

                <Select
                    fullWidth
                    displayEmpty
                    size="small"
                    value={selectedFundId}
                    onChange={(e) => setSelectedFundId(e.target.value)}
                    MenuProps={SELECT_MENU_PROPS}
                    sx={{ marginBottom: '16px' }}
                >
                    <MenuItem value="">— Choose a fund —</MenuItem>
                    {funds.map((f) => (
                        <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                    ))}
                </Select>

                <Box sx={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
                    <TextField
                        size="small"
                        placeholder="Search by Loan ID or borrower name..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        sx={{ flex: 1, minWidth: 220 }}
                    />
                    <Autocomplete
                        size="small"
                        options={institutes}
                        getOptionLabel={(option) => option.brandName || ''}
                        filterOptions={filterInstituteOptions}
                        value={institutes.find((i) => i.id === selectedInstituteId) || null}
                        onChange={(e, value) => setSelectedInstituteId(value?.id || '')}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                            <TextField {...params} placeholder="All Institutes" />
                        )}
                        ListboxProps={{ style: { maxHeight: 250 } }}
                        slotProps={{ popper: AUTOCOMPLETE_POPPER_PROPS }}
                        sx={{ minWidth: 220 }}
                    />
                </Box>

                {loadError && <Alert severity="error" sx={{ marginBottom: '12px' }}>{loadError}</Alert>}

                <Box sx={{ maxHeight: 400, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox checked={allOnPageSelected} onChange={toggleSelectAllOnPage} />
                                </TableCell>
                                <TableCell>Loan ID</TableCell>
                                <TableCell>Borrower</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Tenure</TableCell>
                                <TableCell>EMI</TableCell>
                                <TableCell>Disbursement</TableCell>
                                <TableCell>Institute</TableCell>
                            </TableRow>
                        </TableHead>
                        {isLoadingLoans ? (
                            <LoanTableSkeleton />
                        ) : loans.length === 0 ? (
                            <TableBody>
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ color: 'text.secondary', padding: '24px' }}>
                                        No free loans match the current filters.
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        ) : (
                            <TableBody>
                                {loans.map((loan) => (
                                    <TableRow
                                        key={loan.loanId}
                                        hover
                                        onClick={() => toggleLoan(loan)}
                                        sx={{ cursor: 'pointer', backgroundColor: selectedLoanIds.has(loan.loanId) ? '#eff6ff' : undefined }}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox checked={selectedLoanIds.has(loan.loanId)} onChange={() => toggleLoan(loan)} onClick={(e) => e.stopPropagation()} />
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>{loan.loanId}</TableCell>
                                        <TableCell>{loan.borrowerName || '—'}</TableCell>
                                        <TableCell>{formatToLakhs(loan.amount)}</TableCell>
                                        <TableCell>{loan.tenure ? `${loan.tenure} mo` : '—'}</TableCell>
                                        <TableCell>{formatToLakhs(loan.emi)}</TableCell>
                                        <TableCell>{formatDate(loan.disbursementDate)}</TableCell>
                                        <TableCell>{loan.institute || '—'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        )}
                    </Table>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                    <Typography variant="body2" color="text.secondary">
                        Showing {start}-{end} of {total} free loans
                    </Typography>
                    <Pagination
                        count={Math.max(1, Math.ceil(total / PAGE_LIMIT))}
                        page={page}
                        onChange={(e, value) => setPage(value)}
                        size="small"
                    />
                </Box>

                {selectedCount > 0 && (
                    <Box sx={{ marginTop: '16px', padding: '12px 16px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: '13px', color: '#1e40af', fontWeight: 600 }}>
                            Selected: {selectedCount} loans | Total Value: {formatToLakhs(selectedTotalValue)}
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
                <Button variant="contained" onClick={handleAssign} disabled={!canAssign}>
                    {isSubmitting ? (
                        <CircularProgress size={18} sx={{ color: 'inherit' }} />
                    ) : (
                        `Assign ${selectedCount} Loans to ${selectedFund ? selectedFund.name : '—'}`
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
